import { useState } from 'react'
import { useTestDataForm, COUNTRY_OPTIONS, FINANCIAL_SCENARIO_OPTIONS } from '../hooks/useTestDataForm'
import type { DocType, CountryCode } from '../hooks/useTestDataForm'
import { useDebitCreditSet } from '../hooks/useDebitCreditSet'
import { XML_FORMAT_LABELS, FACTURX_PROFILE_LABELS } from '../hooks/testdata-form/formatLabels'
import { useConfigPresets } from '../hooks/useConfigPresets'
import { LinesEditor } from './LinesEditor'
import { FinancialScenarioPreviewPanel } from './FinancialScenarioPreviewPanel'
import { ErrorInjectionPanel } from './ErrorInjectionPanel'
import { DebitCreditSetPanel } from './DebitCreditSetPanel'
import { ConfigPresetsPanel } from './ConfigPresetsPanel'
import { TestdataEmailModal } from './TestdataEmailModal'
import { TestdataSetEmailModal } from './TestdataSetEmailModal'
import { TestdataHistoryPanel } from './TestdataHistoryPanel'
import { useStore } from '../store'
import { useTranslation } from 'react-i18next'
import type { ConfigPreset, ConfigPresetMode, InjectionSpec } from '../types/testdata.types'

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
      <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4 uppercase tracking-wide">{title}</h3>
      {children}
    </div>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-xs font-medium text-gray-500 dark:text-gray-400">{label}</label>
      {children}
    </div>
  )
}

const inputCls = 'w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-gray-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors'
const selectCls = inputCls

// REQ-TESTDATA-007 (CAP-003 v1.2) — reuses the existing per-scenario i18n
// label keys (no new backend-supplied title text) for the LinesEditor
// locked-state badge and the preview panel's scenario name.
const FINANCIAL_SCENARIO_LABEL_KEY = Object.fromEntries(
  FINANCIAL_SCENARIO_OPTIONS.map(o => [o.value, o.labelKey])
) as Record<string, string>

export function TestDataGeneratorPage() {
  const f = useTestDataForm()
  const { t } = useTranslation()
  const { currentUser } = useStore()
  const [activeTab, setActiveTab] = useState<'form' | 'history'>('form')

  const debitCredit = useDebitCreditSet({
    buildRequestBody: f.buildRequestBody,
    validateForm: f.validateForm,
    form: f.form,
    refreshDocCounter: f.refreshDocCounter,
    ensureCurrentAutoDocumentNumber: f.ensureCurrentAutoDocumentNumber,
  })
  const configPresets = useConfigPresets()

  const currentMode: ConfigPresetMode = debitCredit.enabled ? 'debit_credit_set' : f.injection.enabled ? 'set' : 'single'

  // REQ-TESTDATA-009 — surface the error belonging to the currently active
  // generator mode in the fixed action area. Shared form validation writes to
  // f.error in every mode, so it remains the fallback behind mode-specific
  // generation errors.
  const actionError = f.injection.enabled
    ? (f.injection.error ?? f.error)
    : debitCredit.enabled
    ? (debitCredit.error ?? f.error)
    : f.error

  const dismissActionError = () => {
    f.setError(null)
    if (f.injection.enabled) f.injection.setError(null)
    if (debitCredit.enabled) debitCredit.setError(null)
  }

  const buildCurrentConfig = (): Record<string, unknown> => {
    if (debitCredit.enabled) return debitCredit.buildSetRequestBody()
    if (f.injection.enabled) return { ...f.buildRequestBody(), document_type: 'invoice', xml_format: 'sales005', injections: f.injection.injections }
    return f.buildRequestBody()
  }

  const handleLoadPreset = (preset: ConfigPreset) => {
    f.loadFromHistory(preset.config_json)
    f.injection.setEnabled(false)
    debitCredit.setEnabled(false)
    if (preset.mode === 'set') {
      f.injection.setEnabled(true)
      if (Array.isArray(preset.config_json.injections)) {
        f.injection.setInjections(preset.config_json.injections as InjectionSpec[])
      }
    } else if (preset.mode === 'debit_credit_set') {
      debitCredit.setEnabled(true)
      debitCredit.setCreditDocumentNumber(String(preset.config_json.credit_document_number ?? ''))
      const savedIndex = preset.config_json.credited_line_index
      debitCredit.setCreditedLineIndex(typeof savedIndex === 'number' ? savedIndex : null)
    }
  }

  const handleLoadFromHistory = (params: Record<string, unknown>) => {
    f.loadFromHistory(params)
    setActiveTab('form')
  }

  return (
    <div className="space-y-5 pb-52 sm:pb-40 lg:pb-32">
      <div className="flex gap-1 bg-gray-100 dark:bg-gray-800 rounded-xl p-1 w-fit">
        <button
          onClick={() => setActiveTab('form')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            activeTab === 'form'
              ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm'
              : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
          }`}
        >
          Generator
        </button>
        <button
          onClick={() => setActiveTab('history')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            activeTab === 'history'
              ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm'
              : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
          }`}
        >
          Geschiedenis
        </button>
      </div>

      {activeTab === 'history' ? (
        <TestdataHistoryPanel
          isAdmin={currentUser?.is_admin ?? false}
          onLoadIntoForm={handleLoadFromHistory}
        />
      ) : (<>
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex gap-1 bg-gray-100 dark:bg-gray-800 rounded-xl p-1">
          {(['invoice', 'credit_note', 'order_response', 'despatch_advice'] as DocType[]).map(dt => (
            <button
              key={dt}
              onClick={() => f.setDocType(dt)}
              disabled={f.injection.enabled || debitCredit.enabled}
              title={(f.injection.enabled || debitCredit.enabled) ? t('error_injection.doc_type_locked') : undefined}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                f.docType === dt
                  ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
              } ${(f.injection.enabled || debitCredit.enabled) ? 'opacity-40 cursor-not-allowed' : ''}`}
            >
              {f.docLabels[dt]}
            </button>
          ))}
        </div>
        <select
          value={f.country}
          onChange={e => f.handleCountryChange(e.target.value as CountryCode)}
          className="rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-gray-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
          title={t('test_data_generator.country_select_title')}
        >
          {COUNTRY_OPTIONS.map(o => (
            <option key={o.value} value={o.value}>{o.flag} {o.value} — {o.name}</option>
          ))}
        </select>
        {f.localeOptions.length > 1 && (
          <select
            value={f.activeLocale}
            onChange={e => f.handleLocaleChange(e.target.value)}
            className="rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-gray-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
            title="Taal / locale"
            aria-label="Taal / locale"
          >
            {f.localeOptions.map(option => (
              <option key={option.locale} value={option.locale}>{option.label}</option>
            ))}
          </select>
        )}
        <div className="flex gap-1 bg-gray-100 dark:bg-gray-800 rounded-xl p-1">
          {(['sales005', 'ubl21', 'cii', 'facturx'] as const).map(fmt => {
            const docRestricted = (fmt === 'cii' || fmt === 'facturx')
              && (f.docType === 'order_response' || f.docType === 'despatch_advice')
            const lockedBySet = f.injection.enabled && fmt !== 'sales005'
            const fmtLabel = XML_FORMAT_LABELS[fmt]
            if (lockedBySet) return (
              <button
                key={fmt}
                disabled
                title={t('error_injection.format_locked')}
                className="px-3 py-2 rounded-lg text-sm font-medium text-gray-500 dark:text-gray-400 opacity-40 cursor-not-allowed"
              >
                {fmtLabel}
              </button>
            )
            return (
              <button
                key={fmt}
                onClick={() => !docRestricted && f.setXmlFormat(fmt)}
                disabled={docRestricted}
                title={docRestricted ? t('test_data_generator.format_invoice_creditnote_only') : undefined}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  f.xmlFormat === fmt
                    ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm'
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                } ${docRestricted ? 'opacity-40 cursor-not-allowed' : ''}`}
              >
                {fmtLabel}
              </button>
            )
          })}
        </div>
        {(f.xmlFormat === 'ubl21' || f.xmlFormat === 'cii') && !f.injection.enabled && (
          <select
            value={f.xmlProfile}
            onChange={e => f.setXmlProfile(e.target.value as typeof f.xmlProfile)}
            className="rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-gray-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
            title={t('test_data_generator.xml_profile_title')}
          >
            <option value="none">{t('test_data_generator.xml_profile_none')}</option>
            <option value="cius_fr">{t('test_data_generator.xml_profile_cius_fr')}</option>
            <option value="extended_ctc_fr">{t('test_data_generator.xml_profile_extended_ctc_fr')}</option>
          </select>
        )}
        {f.xmlFormat === 'facturx' && (
          <select
            value={f.facturxProfile}
            onChange={e => f.setFacturxProfile(e.target.value as typeof f.facturxProfile)}
            className="rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-gray-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
            title={t('test_data_generator.facturx_profile_title')}
          >
            {Object.entries(FACTURX_PROFILE_LABELS).map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
        )}
        {f.localeConfig.currency !== 'EUR' && (
          <span className="text-xs font-medium text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg px-2.5 py-1.5">
            {t('test_data_generator.currency_label', { currency: f.localeConfig.currency })}
          </span>
        )}
        {/* REQ-TESTDATA-005 (CAP-003 v1.0) — compact Financial scenario
            selector (§18). Only meaningful for invoice + SALES005 in this
            release; disabled (with an explanation) otherwise rather than
            silently ignored. FIN-MULTIVAT-001 is additionally disabled for a
            country whose VAT profile has no reduced rate (e.g. DK).
            DEC-FIN-001 re-enabled FIN-DOC-DISCOUNT-001/FIN-COMBINED-001: a
            document allowance targets the fixed 'standard' VAT category,
            resolved server-side — no VAT-category selector here, this is not
            user-configurable in v1.0. */}
        <select
          value={f.financialScenarioId ?? ''}
          onChange={e => f.setFinancialScenarioId((e.target.value || undefined) as typeof f.financialScenarioId)}
          disabled={!f.financialScenarioSupported}
          title={
            !f.financialScenarioSupported
              ? t('test_data_generator.financial_scenario.unsupported_combination')
              : t('test_data_generator.financial_scenario.title')
          }
          className="rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-gray-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <option value="">{t('test_data_generator.financial_scenario.normal')}</option>
          {FINANCIAL_SCENARIO_OPTIONS.map(opt => (
            <option
              key={opt.value}
              value={opt.value}
              disabled={opt.disabled || (opt.value === 'FIN-MULTIVAT-001' && f.country === 'DK')}
            >
              {t(opt.labelKey)} ({opt.value})
            </option>
          ))}
        </select>
        {f.financialScenarioId && f.financialScenarioUnsupportedForCountry && (
          <span className="text-xs font-medium text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg px-2.5 py-1.5">
            {t('test_data_generator.financial_scenario.unsupported_country', { country: f.country })}
          </span>
        )}
      </div>

      {f.templates.length > 0 && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 rounded-xl p-4">
          <h3 className="text-xs font-semibold text-blue-700 dark:text-blue-300 uppercase tracking-wide mb-3">{t('test_data_generator.saved_templates')}</h3>
          <div className="flex flex-wrap gap-2">
            {f.templates.map(tpl => (
              <div key={tpl.id} className="flex items-center gap-1 bg-white dark:bg-gray-700 border border-blue-200 dark:border-gray-600 rounded-lg px-2 py-1">
                <button
                  onClick={() => f.loadTemplate(tpl.id)}
                  className="text-xs text-blue-700 dark:text-blue-300 hover:underline font-medium"
                  title={t('test_data_generator.load_template_title', { docLabel: f.uiDocLabels[tpl.document_type as DocType] ?? tpl.document_type })}
                >
                  {tpl.name}
                </button>
                <span className="text-xs text-gray-400">({f.uiDocLabels[tpl.document_type as DocType] ?? tpl.document_type})</span>
                <button
                  onClick={() => f.deleteTemplate(tpl.id)}
                  disabled={f.deletingId === tpl.id}
                  className="ml-1 text-gray-400 hover:text-red-500 text-xs leading-none"
                  title={t('test_data_generator.delete_template')}
                >
                  {f.deletingId === tpl.id ? '…' : '×'}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <ConfigPresetsPanel
        hook={configPresets}
        currentMode={currentMode}
        buildConfig={buildCurrentConfig}
        onLoad={handleLoadPreset}
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <Section title={t('test_data_generator.supplier')}>
          {f.partyPresets.length > 0 && (
            <div className="mb-4">
              <label className="text-xs font-medium text-gray-500 dark:text-gray-400 block mb-1">{t('test_data_generator.select_example')}</label>
              <select
                className={selectCls}
                value={f.selectedSupplierPresetId}
                onChange={e => f.applySupplierPreset(e.target.value)}
              >
                <option value="" disabled>{t('test_data_generator.choose_example')}</option>
                {f.partyPresets.map(p => (
                  <option key={p.id} value={p.id}>{p.label}</option>
                ))}
              </select>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <Field label={t('test_data_generator.field_name_required')}><input className={inputCls} value={f.form.supplier_name} onChange={f.set('supplier_name')} placeholder={t('test_data_generator.placeholder_supplier_name')} /></Field>
            <Field label={t('test_data_generator.field_gln_required')}><input className={inputCls} value={f.form.supplier_gln} onChange={f.set('supplier_gln')} placeholder="8712345000000" /></Field>
            <Field label={t('test_data_generator.field_attn')}><input className={inputCls} value={f.form.supplier_attn} onChange={f.set('supplier_attn')} placeholder={t('test_data_generator.placeholder_contact_name')} /></Field>
            <Field label={t('test_data_generator.field_street')}><input className={inputCls} value={f.form.supplier_address} onChange={f.set('supplier_address')} placeholder={t('test_data_generator.placeholder_street')} /></Field>
            <Field label={t('test_data_generator.field_postal')}><input className={inputCls} value={f.form.supplier_postal} onChange={f.set('supplier_postal')} placeholder="1234 AB" /></Field>
            <Field label={t('test_data_generator.field_city')}><input className={inputCls} value={f.form.supplier_city} onChange={f.set('supplier_city')} placeholder="Amsterdam" /></Field>
            {!f.isDA && <Field label={t('test_data_generator.field_iban')}><input className={inputCls} value={f.form.supplier_iban} onChange={f.set('supplier_iban')} placeholder="NL91ABNA0417164300" /></Field>}
            <Field label={t('test_data_generator.field_vat')}><input className={inputCls} value={f.form.supplier_vat} onChange={f.set('supplier_vat')} placeholder="NL001234567B01" /></Field>
            <Field label={t('test_data_generator.field_kvk')}><input className={inputCls} value={f.form.supplier_kvk} onChange={f.set('supplier_kvk')} placeholder="12345678" /></Field>
            <Field label={t('test_data_generator.field_siren')}><input className={inputCls} value={f.form.supplier_siren} onChange={f.set('supplier_siren')} placeholder="123456789" /></Field>
            <Field label={t('test_data_generator.field_phone')}><input className={inputCls} value={f.form.supplier_phone} onChange={f.set('supplier_phone')} placeholder="+31 20 123 4567" /></Field>
            <Field label={t('test_data_generator.field_email')}><input className={inputCls} value={f.form.supplier_email} onChange={f.set('supplier_email')} placeholder="info@leverancier.nl" /></Field>
            <Field label={t('test_data_generator.field_website')}><input className={inputCls} value={f.form.supplier_website} onChange={f.set('supplier_website')} placeholder="www.leverancier.nl" /></Field>
          </div>

          <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700">
            <label className="text-xs font-medium text-gray-500 dark:text-gray-400 block mb-2">{t('test_data_generator.logo_label')}</label>
            {f.logoPending ? (
              <span className="text-xs text-gray-500 dark:text-gray-400" role="status">{t('test_data_generator.logo_loading_party')}</span>
            ) : f.logoBase64 ? (
              <div className="flex items-center gap-3 flex-wrap">
                <span className="text-xs text-green-700 dark:text-green-400 font-medium">{f.logoFileName}</span>
                {f.logoIsFromSaved && (
                  <span className="text-xs text-blue-600 dark:text-blue-400">{t('test_data_generator.logo_using_saved')}</span>
                )}
                {f.logoIsFromParty && (
                  <span className="text-xs text-blue-600 dark:text-blue-400">{t('test_data_generator.logo_using_party')}</span>
                )}
                <button onClick={f.clearLogo} className="text-xs text-red-500 hover:text-red-700 transition-colors">
                  {t('common.delete')}
                </button>
                {f.hasSavedLogo && !f.logoIsFromParty && (
                  <button onClick={f.removeSavedLogo} disabled={f.logoSaving} className="text-xs text-red-500 hover:text-red-700 transition-colors disabled:opacity-40">
                    {t('test_data_generator.logo_remove_default')}
                  </button>
                )}
              </div>
            ) : (
              <div className="flex items-center gap-3 flex-wrap">
                <label className="inline-flex items-center gap-2 cursor-pointer text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200 border border-blue-200 dark:border-blue-700 rounded-lg px-3 py-1.5 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  {t('test_data_generator.logo_upload')}
                  <input
                    ref={f.logoInputRef}
                    type="file"
                    accept="image/png,image/jpeg"
                    className="hidden"
                    onChange={f.handleLogoUpload}
                  />
                </label>
                {f.hasSavedLogo && (
                  <>
                    <button onClick={f.selectSavedLogo} className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200 border border-blue-200 dark:border-blue-700 rounded-lg px-3 py-1.5 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors">
                      {t('test_data_generator.logo_use_saved')}
                    </button>
                    <button onClick={f.removeSavedLogo} disabled={f.logoSaving} className="text-xs text-red-500 hover:text-red-700 transition-colors disabled:opacity-40">
                      {t('test_data_generator.logo_remove_default')}
                    </button>
                  </>
                )}
              </div>
            )}
            {!f.logoPending && !f.logoIsFromSaved && !f.logoIsFromParty && (
              <label className="mt-2 flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 cursor-pointer">
                <input
                  type="checkbox"
                  checked={f.saveLogoDefault}
                  disabled={f.logoSaving}
                  onChange={e => f.toggleSaveLogoDefault(e.target.checked)}
                  className="rounded"
                />
                {t('test_data_generator.logo_save_default')}
              </label>
            )}
          </div>
        </Section>

        <Section title={t('test_data_generator.buyer')}>
          {f.partyPresets.length > 0 && (
            <div className="mb-4">
              <label className="text-xs font-medium text-gray-500 dark:text-gray-400 block mb-1">{t('test_data_generator.select_example')}</label>
              <select
                className={selectCls}
                value={f.selectedBuyerPresetId}
                onChange={e => f.applyBuyerPreset(e.target.value)}
              >
                <option value="" disabled>{t('test_data_generator.choose_example')}</option>
                {f.partyPresets.map(p => (
                  <option key={p.id} value={p.id}>{p.label}</option>
                ))}
              </select>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <Field label={t('test_data_generator.field_name_required')}><input className={inputCls} value={f.form.buyer_name} onChange={f.set('buyer_name')} placeholder={t('test_data_generator.placeholder_buyer_name')} /></Field>
            <Field label={t('test_data_generator.field_gln_required')}><input className={inputCls} value={f.form.buyer_gln} onChange={f.set('buyer_gln')} placeholder="8712345000001" /></Field>
            <Field label={t('test_data_generator.field_attn')}><input className={inputCls} value={f.form.buyer_attn} onChange={f.set('buyer_attn')} placeholder={t('test_data_generator.placeholder_contact_name')} /></Field>
            <Field label={t('test_data_generator.field_vat')}><input className={inputCls} value={f.form.buyer_vat} onChange={f.set('buyer_vat')} placeholder="NL002345678B01" /></Field>
            <Field label={t('test_data_generator.field_street')}><input className={inputCls} value={f.form.buyer_address} onChange={f.set('buyer_address')} placeholder={t('test_data_generator.placeholder_buyer_street')} /></Field>
            <Field label={t('test_data_generator.field_postal')}><input className={inputCls} value={f.form.buyer_postal} onChange={f.set('buyer_postal')} placeholder="5678 CD" /></Field>
            <div className="col-span-2">
              <Field label={t('test_data_generator.field_city')}><input className={inputCls} value={f.form.buyer_city} onChange={f.set('buyer_city')} placeholder="Rotterdam" /></Field>
            </div>
          </div>
        </Section>

        {(f.isInvoice || f.isCreditNote) && (
          <Section title={t('test_data_generator.invoicer_invoicee_section')}>
            <div className="grid grid-cols-2 gap-3">
              <Field label={t('test_data_generator.field_invoicer_name')}><input className={inputCls} value={f.form.invoicer_name} onChange={f.set('invoicer_name')} /></Field>
              <Field label={t('test_data_generator.field_invoicer_gln')}><input className={inputCls} value={f.form.invoicer_gln} onChange={f.set('invoicer_gln')} /></Field>
              <Field label={t('test_data_generator.field_invoicee_name')}><input className={inputCls} value={f.form.invoicee_name} onChange={f.set('invoicee_name')} /></Field>
              <Field label={t('test_data_generator.field_invoicee_gln')}><input className={inputCls} value={f.form.invoicee_gln} onChange={f.set('invoicee_gln')} /></Field>
            </div>
          </Section>
        )}

        <Section title={t('test_data_generator.delivery_party_section')}>
          <div className="grid grid-cols-2 gap-3">
            <Field label={t('test_data_generator.field_name')}><input className={inputCls} value={f.form.delivery_party_name} onChange={f.set('delivery_party_name')} /></Field>
            <Field label={t('test_data_generator.field_gln')}><input className={inputCls} value={f.form.delivery_party_gln} onChange={f.set('delivery_party_gln')} /></Field>
            {f.isDA && <>
              <Field label={t('test_data_generator.field_ship_from_name')}><input className={inputCls} value={f.form.ship_from_name} onChange={f.set('ship_from_name')} /></Field>
              <Field label={t('test_data_generator.field_ship_from_gln')}><input className={inputCls} value={f.form.ship_from_gln} onChange={f.set('ship_from_gln')} /></Field>
            </>}
          </div>
        </Section>

        <Section title={t('test_data_generator.document_section')}>
          <div className="grid grid-cols-2 gap-3">
            <Field label={t('test_data_generator.field_document_number_required')}>
              <div className="flex gap-1.5">
                <input
                  className={inputCls}
                  value={f.form.document_number}
                  onChange={f.setDocumentNumber}
                  placeholder={f.isInvoice ? 'INV-260623-XX-01' : f.isDA ? 'DA-260623-XX-01' : 'OR-260623-XX-01'}
                />
                <button
                  type="button"
                  title={t('test_data_generator.reset_document_number')}
                  onClick={f.refreshDocCounter}
                  className="shrink-0 px-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-400 hover:text-blue-600 hover:border-blue-400 dark:hover:text-blue-400 dark:hover:border-blue-500 transition-colors"
                >
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                </button>
              </div>
            </Field>
            <Field label={t('test_data_generator.field_date')}><input type="date" className={inputCls} value={f.form.document_date} onChange={f.set('document_date')} /></Field>
            <Field label={t('test_data_generator.field_project_number')}><input className={inputCls} value={f.form.project_number} onChange={f.set('project_number')} /></Field>
            <Field label={t('test_data_generator.field_buyers_order_number')}><input className={inputCls} value={f.form.buyers_order_number} onChange={f.set('buyers_order_number')} /></Field>
            {f.docType === 'order_response' && (
              <Field label={t('test_data_generator.field_suppliers_order_number')}><input className={inputCls} value={f.form.suppliers_order_number} onChange={f.set('suppliers_order_number')} /></Field>
            )}
            {(f.isInvoice || f.isDA) && (
              <Field label={t('test_data_generator.field_or_reference')}><input className={inputCls} value={f.form.order_response_number} onChange={f.set('order_response_number')} /></Field>
            )}
            {f.isInvoice && (
              <Field label={t('test_data_generator.field_da_reference')}><input className={inputCls} value={f.form.desadv_number} onChange={f.set('desadv_number')} /></Field>
            )}
            <Field label={t('test_data_generator.field_end_customer_order_number')}><input className={inputCls} value={f.form.end_customer_order_number} onChange={f.set('end_customer_order_number')} /></Field>
          </div>
        </Section>
      </div>

      <LinesEditor
        lines={f.lines}
        isDA={f.isDA}
        isInvoice={f.isInvoice}
        locale={f.activeLocale}
        vatCodeOptions={f.vatCodeOptions}
        availablePriceLists={f.availablePriceLists}
        randomPriceListId={f.randomPriceListId}
        onRandomPriceListChange={f.setRandomPriceListId}
        randomCount={f.randomCount}
        onRandomCountChange={f.setRandomCount}
        randomLoading={f.randomLoading}
        randomError={f.randomError}
        roundUnitPrices={f.roundUnitPrices}
        onToggleRoundUnitPrices={f.setRoundUnitPrices}
        roundUnitPriceValue={f.roundUnitPriceValue}
        onRoundUnitPriceValueChange={f.setRoundUnitPriceValue}
        compactPdfLines={f.compactPdfLines}
        onToggleCompactPdfLines={f.setCompactPdfLines}
        includeLocaleRecognitionText={f.includeLocaleRecognitionText}
        onToggleIncludeLocaleRecognitionText={f.setIncludeLocaleRecognitionText}
        isFacturx={f.xmlFormat === 'facturx'}
        isSetMode={f.injection.enabled || debitCredit.enabled}
        includeGroundTruth={f.includeGroundTruth}
        onToggleIncludeGroundTruth={f.setIncludeGroundTruth}
        lineGeneration={f.lineGeneration}
        onLineGenerationChange={f.setLineGeneration}
        precisionMaterialized={f.precisionMaterialized}
        isPrecisionStale={f.isPrecisionStale}
        onUpdateLineDecimal={f.updateLineDecimal}
        vatOverrides={f.vatOverrides}
        showVatAdd={f.showVatAdd}
        onToggleVatAdd={f.setShowVatAdd}
        newVatCode={f.newVatCode}
        onNewVatCodeChange={code => { f.setNewVatCode(code); localStorage.setItem('testdata-vat-new-code', code) }}
        newVatPct={f.newVatPct}
        onNewVatPctChange={pct => { f.setNewVatPct(pct); localStorage.setItem('testdata-vat-new-pct', String(pct)) }}
        onAddVatOverride={f.addVatOverride}
        onRemoveVatOverride={f.removeVatOverride}
        onClearVatOverrides={() => f.setVatOverrides([])}
        onGenerateRandomLines={f.generateRandomLines}
        onUpdateLine={f.updateLine}
        onAddLine={f.addLine}
        onRemoveLine={f.removeLine}
        scenarioLock={
          f.financialScenarioActive && f.financialScenarioId
            ? { scenarioId: f.financialScenarioId, labelKey: FINANCIAL_SCENARIO_LABEL_KEY[f.financialScenarioId] }
            : null
        }
      />

      {/* REQ-TESTDATA-007 (CAP-003 v1.2) — Financial Scenario Preview panel:
          a read-only view of the same canonical result already materialized
          into LinesEditor above — never a second/independent calculation. */}
      {f.financialScenarioActive && (
        <FinancialScenarioPreviewPanel
          scenarioId={f.financialScenarioId}
          locale={f.activeLocale}
          preview={f.financialScenarioPreview}
          loading={f.financialScenarioPreviewLoading}
          error={f.financialScenarioPreviewError}
        />
      )}

      <ErrorInjectionPanel
        enabled={f.injection.enabled}
        onToggle={value => {
          if (value) {
            f.setDocType('invoice')
            f.setXmlFormat('sales005')
            debitCredit.setEnabled(false)
            // REQ-TESTDATA-004 (v0.8.1): error-injection sets don't use the
            // precision transform (v0.8 limitation, unchanged) — turn
            // Precision test fully off rather than leaving it stale-but-hidden,
            // which would otherwise block error-injection generation too.
            f.setLineGeneration({ mode: 'catalogue' })
          }
          f.injection.setEnabled(value)
        }}
        injections={f.injection.injections}
        onAdd={f.injection.addInjection}
        onRemove={f.injection.removeInjection}
        onUpdate={f.injection.updateInjection}
        lines={f.lines}
        result={f.injection.result}
        onClearResult={f.injection.clearResult}
      />

      <DebitCreditSetPanel
        enabled={debitCredit.enabled}
        onToggle={value => {
          if (value) {
            f.setDocType('invoice')
            f.injection.setEnabled(false)
            // REQ-TESTDATA-004 (v0.8.1) — see ErrorInjectionPanel.onToggle above.
            f.setLineGeneration({ mode: 'catalogue' })
          }
          debitCredit.setEnabled(value)
        }}
        creditDocumentNumber={debitCredit.creditDocumentNumber}
        onCreditDocumentNumberChange={debitCredit.setCreditDocumentNumber}
        creditedLineIndex={debitCredit.creditedLineIndex}
        onCreditedLineIndexChange={debitCredit.setCreditedLineIndex}
        form={f.form}
        lines={f.lines}
        locale={f.activeLocale}
        result={debitCredit.result}
        onClearResult={debitCredit.clearResult}
      />

      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
        <label className="flex items-center gap-2 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={f.saveAsTemplate}
            onChange={e => f.setSaveAsTemplate(e.target.checked)}
            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('test_data_generator.save_as_template_checkbox')}</span>
        </label>
        {f.saveAsTemplate && (
          <div className="mt-3 max-w-sm">
            <Field label={t('test_data_generator.template_name_label')}>
              <input
                className={inputCls}
                value={f.templateName}
                onChange={e => f.setTemplateName(e.target.value)}
                placeholder={t('test_data_generator.template_name_placeholder')}
              />
            </Field>
          </div>
        )}
      </div>

      {f.injection.error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 rounded-xl px-4 py-3 text-sm flex items-start justify-between">
          <span>{f.injection.error}</span>
          <button onClick={() => f.injection.setError(null)} className="ml-4 text-red-400 hover:text-red-600">×</button>
        </div>
      )}
      {debitCredit.error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 rounded-xl px-4 py-3 text-sm flex items-start justify-between">
          <span>{debitCredit.error}</span>
          <button onClick={() => debitCredit.setError(null)} className="ml-4 text-red-400 hover:text-red-600">×</button>
        </div>
      )}
      {f.error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 rounded-xl px-4 py-3 text-sm flex items-start justify-between">
          <span>{f.error}</span>
          <button onClick={() => f.setError(null)} className="ml-4 text-red-400 hover:text-red-600">×</button>
        </div>
      )}
      {f.successMsg && (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-700 dark:text-green-300 rounded-xl px-4 py-3 text-sm flex items-start justify-between">
          <span>{f.successMsg}</span>
          <button onClick={() => f.setSuccessMsg(null)} className="ml-4 text-green-400 hover:text-green-600">×</button>
        </div>
      )}

      <div className="fixed inset-x-0 bottom-0 z-30 border-t border-gray-200/80 bg-[#F0F7FB]/95 px-4 py-3 shadow-[0_-8px_20px_-18px_rgba(15,23,42,0.45)] backdrop-blur sm:px-6 dark:border-gray-700/80 dark:bg-gray-950/95">
        <div className="mx-auto flex max-w-6xl flex-col gap-2 lg:flex-row lg:items-center">
          {actionError && (
            <div
              role="alert"
              aria-live="assertive"
              className="flex min-w-0 flex-1 items-start gap-2 rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800 dark:border-red-700 dark:bg-red-900/30 dark:text-red-200"
            >
              <svg
                aria-hidden="true"
                className="mt-0.5 h-4 w-4 shrink-0"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v4m0 4h.01M10.3 3.7L2.7 17a2 2 0 001.7 3h15.2a2 2 0 001.7-3L13.7 3.7a2 2 0 00-3.4 0z" />
              </svg>
              <span className="min-w-0 flex-1 break-words">{actionError}</span>
              <button
                type="button"
                onClick={dismissActionError}
                aria-label="Foutmelding sluiten"
                title="Foutmelding sluiten"
                className="shrink-0 rounded px-1 font-semibold text-red-600 hover:bg-red-100 hover:text-red-800 focus:outline-none focus:ring-2 focus:ring-red-500 dark:text-red-300 dark:hover:bg-red-900/50 dark:hover:text-red-100"
              >
                ×
              </button>
            </div>
          )}

          <div className="ml-auto flex flex-wrap justify-end gap-3">
          {!f.injection.enabled && <button
            onClick={debitCredit.enabled ? debitCredit.openEmailModal : f.openEmailModal}
            disabled={f.loading || debitCredit.loading || (!debitCredit.enabled && f.isPrecisionStale)}
            title={(!debitCredit.enabled && f.isPrecisionStale) ? (t('linesEditor.precision_stale_error') as string) : undefined}
            className="flex items-center gap-2 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 text-sm font-semibold rounded-xl px-5 py-3 transition-colors shadow-sm"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            {t('test_data_generator.email_button')}
          </button>}
          <button
            onClick={debitCredit.enabled ? debitCredit.generateSet : f.injection.enabled ? f.injection.generateSet : f.generate}
            disabled={f.loading || f.injection.loading || debitCredit.loading || (!debitCredit.enabled && !f.injection.enabled && f.isPrecisionStale)}
            title={(!debitCredit.enabled && !f.injection.enabled && f.isPrecisionStale) ? (t('linesEditor.precision_stale_error') as string) : undefined}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white text-sm font-semibold rounded-xl px-6 py-3 transition-colors shadow-sm"
          >
            {(f.loading || f.injection.loading || debitCredit.loading) ? (
              <>
                <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                </svg>
                {t('common.busy')}
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                {debitCredit.enabled
                  ? t('debit_credit_set.generate_button')
                  : f.injection.enabled
                  ? t('error_injection.generate_set_button')
                  : t('test_data_generator.generate_button', { docLabel: f.docLabels[f.docType] })}
              </>
            )}
          </button>
          </div>
        </div>
      </div>

      {f.showEmailModal && f.emailSnapshot && (
        <TestdataEmailModal
          requestBody={f.emailSnapshot}
          resolveDocumentNumber={f.ensureCurrentAutoDocumentNumber}
          onClose={f.closeEmailModal}
          onSent={f.refreshDocCounter}
        />
      )}

      {debitCredit.showEmailModal && (
        <TestdataSetEmailModal
          endpoint="/api/testdata/send-debit-credit-set-email"
          requestBody={debitCredit.buildSetRequestBody()}
          resolveDynamicFields={debitCredit.resolveDynamicFields}
          documents={[
            { documentType: 'invoice', documentNumber: f.form.document_number },
            { documentType: 'credit_note', documentNumber: debitCredit.creditDocumentNumber },
          ]}
          onClose={() => debitCredit.setShowEmailModal(false)}
          onSent={f.refreshDocCounter}
        />
      )}
      </>)}
    </div>
  )
}
